import {excelFileToRows} from 'common/HelperFunctions'
import ColumnAssignmentModal from 'common/components/dataUpload/ColumnAssignmentModal'
import SheetAssignmentModal from 'common/components/dataUpload/SheetAssignmentModal'
import {
  dispatchApi,
  forgetUploadedFile,
  isAbortError,
  isChunkedUploadAvailable,
  uploadFileInPieces,
  uploadedIdOf,
} from 'common/helpers/chunkedUpload'
import {
  DATA_UPLOAD_ERROR_KEYS,
  type DataUploadAllocationAssignment,
  type DataUploadAllocationQuestion,
  type DataUploadAssignmentQuestion,
  type DataUploadBranchAssignments,
  type DataUploadBranchQuestion,
  type DataUploadColumnAssignments,
  type DataUploadColumnQuestions,
  type DataUploadCounts,
  type DataUploadSheetAssignments,
  type DataUploadSkippedRow,
  type DataUploadSubjectMapping,
  type DataUploadSubjectMappings,
  MIXED_BRANCH,
  allocationAnswerKey,
  appendAllocationAssignments,
  appendBranchAssignments,
  appendColumnAssignments,
  appendSheetAssignments,
  dataUploadCountsSummary,
  dataUploadErrorCode,
  dataUploadErrorIsInfo,
  dataUploadErrorMessage,
  dataUploadRetryable,
  dataUploadSkippedRowKey,
  dataUploadTemplateToFile,
  normalizeDataUploadConversion,
  pendingQuestionsText,
  rejectedSubjectAliasCount,
  subjectAliasesBody,
  withConfirmedSubjectsApplied,
  allocationLineCounts,
  askableAllocations,
  withoutAnsweredAllocations,
  withoutAnsweredBranches,
  withoutAnsweredColumns,
  withoutConfirmedSubjects,
} from 'common/helpers/dataUpload'
import {
  isDataUploadConversionAvailable,
  useDataUploadConfig,
} from 'common/hooks/useDataUploadConfig'
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  FileSpreadsheet,
  FileText,
  Image as ImageIcon,
  Info,
} from 'lucide-react'
import React, {FC, useCallback, useEffect, useId, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch} from 'react-redux'
import {toast} from 'react-toastify'
import {
  getDataUploadDraft,
  getDataUploadDraftTemplate,
  putDataUploadSubjectAliases,
} from 'setup/redux/Actions/DataUpload/dataUpload'
import {extractOnboardingTeachers} from 'setup/redux/Actions/Onboarding/extractOnboardingTeachers'
import {prefillOnboardingFromFile} from 'setup/redux/Actions/Onboarding/prefillOnboardingFromFile'

import {discardPrefillDraftIds, waitForPrefillJob} from '../hooks/useOnboardingPrefillDraft'
import {STEP_TITLE_KEYS} from '../utils/stepTitles'
import OnboardingUploader, {
  type UploaderFile,
  type UploaderFileState,
  type UploaderHead,
} from './upload/OnboardingUploader'
import {
  REVIEW_DISABLED_WHILE_CHECKING,
  TEACHER_FILE_SUMMARY_ON_REVIEW,
  TEACHER_UPLOAD_EXTRACTION_ENABLED,
  TEACHER_UPLOAD_LIMITS,
  TeacherEntryRow,
  TeacherExtraction,
  TeacherFileCounts,
  type TeacherFileExtra,
  TeacherFileKind,
  TeacherFileRejection,
  classifyTeacherFile,
  fileExtension,
  formatBytes,
  namesToTeacherRows,
  normalizeExtractionResponse,
  normalizePastedNames,
  parseCsvRows,
  parseTeachersRows,
  type TeacherPrefillRequest,
  prefillDraftIdsOf,
  splitTeacherNames,
  teacherUploadAccept,
  validateTeacherFiles,
} from '../utils/teacherEntry'
import {ONBOARDING_QA_OCT18} from '../utils/onboardingQaOct18'

/**
 * "Add Teachers" entry screen of the Teacher Allocation step.
 *
 * Two ways in, one way out: names typed/pasted into the numbered field and files dropped
 * on the upload card are both turned into review rows, merged, and handed to the parent
 * through `onReview` when the user presses Review Teachers. The parent (Step4Manual)
 * appends them to `manualData.teachers` and switches to its review table, where duplicate
 * names and low-confidence extractions are resolved.
 *
 * Files are processed as soon as they are added. On the AI servers every file (spreadsheets
 * too) is uploaded in 512 KB pieces and read by `onboarding/extract-teachers`: a sheet with
 * headers is read without the model in about 2 s, and every row rule stays on the server. The
 * same upload is handed to `onboarding/prefill`, which reads it again in the background for the
 * Students and Teacher Allocation steps. Elsewhere the upload stays Excel-only and the sheet is
 * parsed in the browser, with the same row rules (utils/recordRules.ts).
 */

interface Props {
  isDark: boolean
  /** Teachers already in the list (resume, or coming back from Review to add more). */
  existingCount: number
  showBackButton: boolean
  onBack: () => void
  /**
   * `counts`: the students and allocation lines the files hold for the later steps (the
   * summary's badges), or null when the files were not checked for them. Review shows them as
   * stat cards.
   */
  onReview: (rows: TeacherEntryRow[], counts: TeacherFileCounts | null) => void
  onDownloadTemplate: () => void
  /**
   * Every "which classes?" answer given so far, after each one. The Allocation step fetches the
   * Teachers file's allocations with them, so what was answered here is not asked again.
   */
  onAllocationAnswers?: (answers: DataUploadAllocationAssignment[]) => void
  /**
   * The last read's "Which classes?" lines when some were confirmed with no classes and still
   * wait (`pending`), else []: Review Teachers shows them as "N need review".
   */
  onPendingAllocations?: (questions: DataUploadAllocationQuestion[]) => void
  /** Each `onboarding/prefill` this screen sends, with its drafts (see TeacherPrefillRequest). */
  onPrefillRequest?: (request: TeacherPrefillRequest) => void
  /**
   * Set to a reader of what Review Teachers would take right now (typed names, files already
   * read), or null when there is nothing. "Save and finish later" adds those rows to the list,
   * since this screen's own state is lost with the page.
   */
  pendingRowsRef?: React.MutableRefObject<
    (() => {rows: TeacherEntryRow[]; counts: TeacherFileCounts | null} | null) | null
  >
  /**
   * The students and allocation lines the files hold, once the prefill has counted them. Review
   * no longer waits for that count (QA 13 Oct, #366): it opens as soon as the names are read,
   * and its stat cards fill in when this arrives.
   */
  onFileCounts?: (counts: TeacherFileCounts | null) => void
  /**
   * Teachers a Teacher Allocation file held with no assignments (`carried.teachers`): shown as
   * that file's line, read from the draft (`drafts/:id/template`), never uploaded again.
   */
  carriedTeachers?: {file: File; draftId: string} | null
  /** The carried draft is on this screen now: not handed over again. */
  onCarriedTeachersTaken?: (draftId: string) => void
  /** The carried draft is done with: its teachers went to Review, or its file was removed. */
  onCarriedDraftDone?: (draftId: string) => void
}

type UploadStatus =
  | 'parsing'
  | 'uploading'
  | 'extracting'
  | 'ready'
  | 'error'
  /** Waiting for the user to say whose list each sheet of the workbook is. */
  | 'needs-assignment'
  /** Given to another screen on the user's answer: an info line, never an error. */
  | 'assigned'

interface UploadItem {
  id: string
  file: File
  kind: TeacherFileKind
  status: UploadStatus
  rows: TeacherEntryRow[]
  /** 0..1 while `status === 'uploading'`. */
  progress?: number
  /** i18n id of the failure message when `status === 'error'`. */
  errorKey?: string
  /** Already-translated failure copy (a backend code mapped to text); wins over `errorKey`. */
  errorText?: string
  /** What was read: "Read 52 rows. 50 teachers are ready. 1 line is a heading…". */
  summary?: string | null
  /** Rows left out of the file, with the reason. */
  skipped?: DataUploadSkippedRow[]
  /** Set when `status === 'assigned'`: the screen the user gave the file to, or 'ignore'. */
  assignedTo?: string
  /** `file` when the file itself said whose list it is; absent when the user answered. */
  assignedBy?: string
  /**
   * Read, but held no teachers while the request's other files did: a neutral line, not the
   * red "No teacher names were found" (that stays for a request that found nobody).
   */
  noTeachers?: boolean
  /**
   * A file read on the server: how many teachers it gave (`sources[].records`) and how many of
   * them need a look. Its rows are in the one merged list of the request, not on the file.
   */
  found?: number
  needsCheck?: number
  /** Read, but some rows were left out (`sources[].partial`, else left-out rows present). */
  partial?: boolean
  /** How many rows were left out (`sources[].leftOutCount`). */
  leftOutCount?: number
  /** The failure's code, when `status === 'error'`. */
  errorCode?: string
  /** False when trying the same file again cannot help: "Replace file" instead of "Try again". */
  retryable?: boolean
  /**
   * Read on Teacher Allocation and carried here (`carried.teachers.draftId`): its rows come from
   * that draft, so it is never sent with the files' server read.
   */
  carriedDraftId?: string
}

/** Rows listed under a file before "…and N more". */
const MAX_LISTED_SKIPPED = 50

const useMockExtraction =
  String(import.meta.env.VITE_MOCK_ONBOARDING_TEACHER_EXTRACTION ?? '').toLowerCase() === 'true'

/** Why one file was not added, shown next to its name (`{limit}`: the limit it broke). */
const REJECTION_KEYS: Record<TeacherFileRejection, string> = {
  unsupported: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_UNSUPPORTED',
  'too-large': 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_REJECT_TOO_LARGE',
  'too-many': 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_REJECT_TOO_MANY',
  duplicate: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_DUPLICATE',
  'total-too-large': 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_REJECT_TOTAL',
}

/** A file refused when it was added: listed under the upload until dismissed or replaced. */
interface RejectedFile {
  name: string
  text: string
}

/** `counts` for a sheet the browser parsed, so it gets the same summary as a server read. */
const browserCounts = (kept: number, skipped: DataUploadSkippedRow[]): DataUploadCounts => {
  const leftOut: Record<string, number> = {}
  skipped.forEach((row) => {
    const code = row.code ?? row.reason ?? ''
    if (code) leftOut[code] = (leftOut[code] ?? 0) + 1
  })
  // Nothing the browser parses needs a value filled in, so every kept row is ready.
  return {read: kept + skipped.length, kept, ready: kept, leftOut, needValues: {}}
}

const ERROR_KEYS = {
  read: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_READ',
  noNames: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_NO_NAMES',
  noHeader: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_NO_HEADER',
  extract: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_ERROR_EXTRACT',
} as const

/** The questions dialog when no branch is asked about: one list, not a new one per render. */
const NO_BRANCH_QUESTIONS: DataUploadBranchQuestion[] = []

/** The questions of a read, as the dialog asks them (see `questions` in the component). */
interface ReadQuestions {
  columns: DataUploadColumnQuestions | null
  branches: DataUploadBranchQuestion[]
  subjects: DataUploadSubjectMappings | null
  /** Teachers whose subjects came with no class: which classes each subject is taught in. */
  allocations?: DataUploadAllocationQuestion[]
  /** Reopened to change earlier branch answers: the cards start from them. */
  initialBranches?: DataUploadBranchAssignments
}

/** The teacher + subject lines the questions ask classes for. */
const allocationLinesAsked = (questions: ReadQuestions | null): number =>
  (questions?.allocations ?? []).reduce(
    (sum, question) => sum + question.subjects.filter((entry) => entry.subject).length,
    0
  )

/**
 * How many questions are still open: each column, unanswered branch, subject proposal and
 * teacher + subject waiting for classes. Branches reopened to be changed are answered already.
 */
const openQuestionCount = (questions: ReadQuestions | null): number => {
  if (!questions) return 0
  const answeredBranches = questions.initialBranches ?? {}
  return (
    (questions.columns?.questions.length ?? 0) +
    questions.branches.filter((question) => !(question.key in answeredBranches)).length +
    (questions.subjects?.suggestions.length ?? 0) +
    allocationLinesAsked(questions)
  )
}

/**
 * The count of the prefill drafts now running. Module-wide, not per screen: the count outlives
 * the screen (Review opens while it runs), and the screen shown again on "Add more" is a new
 * one — its first read must still stop the old count, so a stale total never reaches Review.
 */
const carriedWatch: {current: AbortController | null} = {current: null}

let uploadSeq = 0
const nextUploadId = () => `teacher-upload-${Date.now()}-${++uploadSeq}`

/** Fake extraction result for local UI work while the endpoint is being built. */
const buildMockExtraction = (fileName: string) => ({
  files: [
    {
      fileName,
      status: 'ok',
      teachers: [
        {name: 'Emma Wilson', employeeId: 'EMP-101', subject: 'Mathematics', confidence: 0.97},
        {
          name: 'Oliver Bennett',
          employeeId: '',
          subject: 'Physics',
          class: 'Grade 9',
          confidence: 0.91,
        },
        {name: 'Sophie Tumer', employeeId: 'EMP-1O3', confidence: 0.54},
      ],
    },
  ],
})

const FileKindIcon: FC<{kind: TeacherFileKind; size?: number}> = ({kind, size = 18}) => {
  if (kind === 'spreadsheet') return <FileSpreadsheet size={size} aria-hidden />
  if (kind === 'image') return <ImageIcon size={size} aria-hidden />
  return <FileText size={size} aria-hidden />
}

const Step4TeacherEntry: FC<Props> = ({
  isDark,
  existingCount,
  showBackButton,
  onBack,
  onReview,
  onDownloadTemplate,
  onAllocationAnswers,
  onPendingAllocations,
  onPrefillRequest,
  pendingRowsRef,
  onFileCounts,
  carriedTeachers,
  onCarriedTeachersTaken,
  onCarriedDraftDone,
}) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const textareaId = useId()
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const gutterRef = useRef<HTMLDivElement>(null)

  const [namesText, setNamesText] = useState('')
  const [files, setFiles] = useState<UploadItem[]>([])
  /** Files refused on the last add (type, size, count or total size), with the reason. */
  const [rejectedFiles, setRejectedFiles] = useState<RejectedFile[]>([])
  /**
   * A server read of the files is in flight. Files that were already read keep showing their
   * result meanwhile (a new file joins the list, it does not reset it), so their statuses alone
   * cannot tell that the merged list is being rebuilt.
   */
  const [readInFlight, setReadInFlight] = useState(false)
  /**
   * The merged list is being rebuilt in the background after a file was removed: the rows on
   * screen stay (they were filtered at once), only Review waits for the fresh list.
   */
  const [refreshInFlight, setRefreshInFlight] = useState(false)
  /**
   * The one "whose list is this?" question for every file: they are read in one request, so the
   * server asks once, covering each file that needs it (keys `<fileName>::<sheet>`). Closing the
   * dialog without answering leaves the files waiting; their "Answer" link opens it again.
   */
  const [sheetQuestion, setSheetQuestion] = useState<DataUploadAssignmentQuestion | null>(null)
  const [sheetQuestionOpen, setSheetQuestionOpen] = useState(false)
  /**
   * Every sheet answer given so far. The server keeps none: a read without `sheetAssignments`
   * has it ask again, so they go with every read of the files.
   */
  const sheetAnswersRef = useRef<DataUploadSheetAssignments>({})
  /**
   * The questions of the last read, asked in one dialog after its rows appear (they are read
   * either way): "which field is this column?", "which branch are these students in?" and
   * subjects the files write under a name the school does not use. Column answers and confirmed
   * subjects are saved for the school; every answer goes with every later read.
   */
  const [questions, setQuestions] = useState<ReadQuestions | null>(null)
  /**
   * The questions dialog is open. Apart from `questions`: its X closes it without answering, the
   * questions stay pending (nothing is sent) and "Answer questions" opens the same dialog again.
   * Unanswered ones are sent nowhere on Review; the Teacher Allocation step asks them later.
   */
  const [questionsOpen, setQuestionsOpen] = useState(false)
  /**
   * The branch questions answered so far, so an answer can be changed later ("Both"
   * instead of a branch): the server never asks an answered sheet again.
   */
  const [branchAnswered, setBranchAnswered] = useState<{
    questions: DataUploadBranchQuestion[]
    answers: DataUploadBranchAssignments
  }>({questions: [], answers: {}})
  const [answeringQuestions, setAnsweringQuestions] = useState(false)
  const columnAnswersRef = useRef<DataUploadColumnAssignments>({})
  /**
   * Every branch answer given so far (sheet key -> branch id, or "mixed"). Not saved per school:
   * the branch is a fact about this upload, so they go with every read, like the sheet answers.
   */
  const branchAnswersRef = useRef<DataUploadBranchAssignments>({})
  /**
   * Every teacher + subject answered so far with its classes (empty: not allocated). Sent to
   * `extract-teachers` (it stops asking those) and to `prefill` (it writes one allocation line
   * per picked class), on every read.
   */
  const allocationAnswersRef = useRef<DataUploadAllocationAssignment[]>([])
  /**
   * "Save teachers only" was chosen: their classes are not asked here (nor sent to
   * `extract-teachers` or `prefill`). The Teacher Allocation step asks them instead, from the
   * prefill draft's own `allocationQuestions`.
   */
  const teachersOnlyRef = useRef(false)
  /**
   * Subject mappings confirmed so far, by lower-cased written name. Saved as aliases, so the
   * next read normally reports them as `applied`; a rejected or slow alias can come back as a
   * suggestion, and this keeps it from being asked again.
   */
  const subjectAnswersRef = useRef<Map<string, DataUploadSubjectMapping>>(new Map())
  /**
   * Branch question keys and subject proposals (lower-cased written name) the user skipped. A
   * branch has no skip value the server keeps, and a skipped proposal is not saved as an alias,
   * so a later read brings both back: remembered here, they are not asked again. (Skipped columns
   * go with the column answers as `ignore`, skipped classes with the allocation answers.)
   */
  const skippedBranchKeysRef = useRef<Set<string>>(new Set())
  const skippedSubjectsRef = useRef<Set<string>>(new Set())
  /**
   * The teachers of the last server read of the files. All of them go in ONE request, so the
   * server merges a teacher named in two files into one row; the list belongs to the request.
   */
  const [serverRows, setServerRows] = useState<TeacherEntryRow[]>([])
  /**
   * What else the files hold, read by `onboarding/prefill` for the later steps: `checking`
   * while its drafts are being read, then how many students / allocations were found.
   */
  const [carried, setCarried] = useState<{
    checking: boolean
    /** Per file (lower-cased name): what that file contributed to each draft. */
    byFile: Record<string, TeacherFileExtra>
  } | null>(null)

  // Word / PDF / photo extraction needs the AI server's endpoint (or the local mock).
  const extractionEnabled =
    useMockExtraction || (TEACHER_UPLOAD_EXTRACTION_ENABLED && isDataUploadConversionAvailable())
  // Limits come from `data-upload/use-cases`, never hardcoded; defaults until it answers.
  const uploadConfig = useDataUploadConfig('teachers', extractionEnabled && !useMockExtraction)
  const maxFileBytes = extractionEnabled
    ? uploadConfig.maxFileBytes
    : TEACHER_UPLOAD_LIMITS.maxFileBytes
  /**
   * How many files may be queued, from the same `data-upload/use-cases` limits the server
   * enforces. Without extraction the sheet is parsed in the browser, one at a time.
   *
   * On the AI servers each file is uploaded in pieces, then all of them are read in ONE
   * `extract-teachers` request (`uploadIds`), so the server merges a teacher who is in two files.
   */
  const maxFiles = extractionEnabled ? Math.max(1, uploadConfig.maxFiles) : 1
  const singleFile = maxFiles === 1
  /**
   * Cap on all the selected files together. The server's `limits.maxTotalBytes` when it sends
   * one (the proposed 10 files · 10 MB each · 50 MB total is set there, once confirmed against
   * real school files); otherwise what the per-file and count limits already allow.
   */
  const maxTotalBytes =
    (extractionEnabled ? uploadConfig.maxTotalBytes : undefined) ?? maxFileBytes * maxFiles
  /** Files are uploaded in pieces and read together on the server. */
  const serverRead = extractionEnabled && !useMockExtraction && isChunkedUploadAvailable()

  const names = useMemo(() => splitTeacherNames(namesText), [namesText])
  const lineCount = namesText.length === 0 ? 0 : namesText.split('\n').length
  const fileRowCount =
    files.reduce((sum, f) => sum + (f.status === 'ready' ? f.rows.length : 0), 0) +
    serverRows.length
  const isFileBusy = (f: UploadItem) =>
    f.status === 'parsing' || f.status === 'uploading' || f.status === 'extracting'
  const isBusy = readInFlight || files.some(isFileBusy)
  const hasInput = names.length > 0 || fileRowCount > 0 || existingCount > 0
  // A file waiting to be assigned has been read by nothing yet; reviewing now would silently
  // drop it, so the question is answered (or the file removed) first.
  const awaitingAssignment = files.some((f) => f.status === 'needs-assignment')
  // The file is still being checked for students and teacher allocations (the prefill). The wait
  // ends with the prefill job (done, failed, or given up on by `waitForPrefillJob`).
  const carriedChecking = Boolean(carried?.checking) && !awaitingAssignment
  /**
   * What Review waits on that is not the user's to do: the background rebuild after a file is
   * removed (the list on screen is already right), and the check above (so its counts reach
   * Review's cards). A click while they run waits for them, with a spinner on the button, and
   * goes on by itself (bugs 363 / 366) — except the check, which disables Review outright while
   * `REVIEW_DISABLED_WHILE_CHECKING` is on.
   */
  const reviewWaitsOn = refreshInFlight || carriedChecking
  const canReview =
    hasInput &&
    !isBusy &&
    !awaitingAssignment &&
    !(REVIEW_DISABLED_WHILE_CHECKING && carriedChecking)

  // --- Typed / pasted names -------------------------------------------------------

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const normalized = normalizePastedNames(e.clipboardData.getData('text'))
    if (normalized === null) return
    e.preventDefault()
    const el = e.currentTarget
    const start = el.selectionStart
    const end = el.selectionEnd
    const before = namesText.slice(0, start)
    const after = namesText.slice(end)
    // Pasted names always land on their own lines, whatever the caret was touching.
    const prefix = before && !before.endsWith('\n') ? '\n' : ''
    const suffix = after && !after.startsWith('\n') ? '\n' : ''
    const inserted = prefix + normalized + suffix
    setNamesText(before + inserted + after)
    const caret = start + inserted.length
    requestAnimationFrame(() => el.setSelectionRange(caret, caret))
  }

  const syncGutterScroll = () => {
    if (gutterRef.current && textareaRef.current) {
      gutterRef.current.scrollTop = textareaRef.current.scrollTop
    }
  }

  // --- Files ----------------------------------------------------------------------

  const patchFile = useCallback((id: string, patch: Partial<UploadItem>) => {
    setFiles((prev) => prev.map((f) => (f.id === id ? {...f, ...patch} : f)))
  }, [])

  /** The server read in flight: a newer read (a file added or removed) stops it. */
  const readAbortRef = useRef<AbortController | null>(null)
  /** Stops watching the prefill drafts when the files change (shared: see `carriedWatch`). */
  const prefillAbortRef = carriedWatch
  /** Counts server reads: a prefill answer from an older read than the latest is stale. */
  const readSeqRef = useRef(0)
  /** The prefill drafts of the latest read (Students / Allocation), deleted once superseded. */
  const prefillDraftIdsRef = useRef<string[]>([])
  /**
   * Leaving the screen stops a read, but not the count of the prefill drafts: Review opens while
   * that count is still running (it no longer waits for it), and this screen unmounts then. The
   * count goes on and reports to the parent (`onFileCounts`), so Review's Students / Allocation
   * cards still fill in. A newer read of the files stops it, as before.
   */
  useEffect(
    () => () => {
      readAbortRef.current?.abort()
    },
    []
  )
  // The latest file list and callback, for a count that lands after this screen unmounted.
  const filesRef = useRef(files)
  const onFileCountsRef = useRef(onFileCounts)
  useEffect(() => {
    filesRef.current = files
    onFileCountsRef.current = onFileCounts
  })

  /**
   * After `onboarding/prefill` answers, wait for its Students and Allocation drafts and say
   * how many rows each holds, so the user knows the files will fill those steps too. Only a
   * count: the steps themselves fetch and apply the drafts when they open.
   */
  const watchCarried = useCallback(
    (prefillResponse: any) => {
      const raw = prefillResponse?.result?.drafts ?? prefillResponse?.drafts ?? {}
      // `{students: {id, job}}`, or the draft id alone.
      const draftOf = (value: any) =>
        typeof value === 'string' || typeof value === 'number'
          ? {id: value}
          : value?.id || value?.draftId
            ? {id: value.id ?? value.draftId, job: value.job}
            : null
      const drafts = {students: draftOf(raw.students), allocation: draftOf(raw.allocation)}
      if (!drafts.students && !drafts.allocation) {
        // Nothing to wait for: the summary can show.
        setCarried(null)
        return
      }
      prefillAbortRef.current?.abort()
      const controller = new AbortController()
      prefillAbortRef.current = controller
      const cancelled = () => controller.signal.aborted
      setCarried({checking: true, byFile: {}})

      /**
       * Rows per file once the job is done. One prefill reads every file, so its job only has
       * the total; the draft's own `files[]` says what each file contributed
       * (`counts.records`). No template call here, which would also re-check allocations
       * against teachers that aren't saved yet.
       */
      const rowsPerFile = async (
        draft: any,
        countOf: (counts: any) => Record<string, unknown> = (counts) => ({records: counts?.records})
      ): Promise<Map<string, Record<string, number>>> => {
        const perFile = new Map<string, Record<string, number>>()
        if (!draft?.id) return perFile
        const job = await waitForPrefillJob(dispatch, String(draft.id), cancelled)
        if (!job || cancelled()) return perFile
        const full = await dispatchApi(dispatch, (ok, fail) =>
          getDataUploadDraft(String(draft.id), ok, fail)
        ).catch(() => null)
        const body = full?.result ?? full
        const draftFiles: any[] = body?.draft?.files ?? body?.files ?? []
        draftFiles.forEach((file) => {
          const name = String(file?.fileName ?? '')
            .trim()
            .toLowerCase()
          if (!name) return
          const sum = {...perFile.get(name)}
          Object.entries(countOf(file?.counts)).forEach(([key, value]) => {
            const count = Number(value ?? 0)
            if (count > 0) sum[key] = (sum[key] ?? 0) + count
          })
          if ((sum.records ?? 0) > 0) perFile.set(name, sum)
        })
        return perFile
      }
      Promise.all([
        rowsPerFile(drafts.students),
        // An allocation line can fill several class-subject cells ("Grade 6 A & B"): the count
        // is the cells the grid fills (`assignments`), not the lines read (`records`).
        // A re-upload also says how many of those cells already have that teacher, and how many
        // are new (`alreadyAssigned` / `newAssignments`, AI server).
        rowsPerFile(drafts.allocation, (counts) => ({
          records: counts?.assignments ?? counts?.records,
          alreadyAssigned: counts?.alreadyAssigned,
          newAssignments: counts?.newAssignments,
          // Proposed cells waiting for "Which classes?" and lines with no proposal (AI server).
          awaitingReview: counts?.awaitingReview,
          unresolvedLines: counts?.unresolvedLines,
        })),
      ]).then(
        ([students, allocation]) => {
          if (cancelled()) return
          const byFile: Record<string, TeacherFileExtra> = {}
          new Set([...students.keys(), ...allocation.keys()]).forEach((name) => {
            const lines = allocation.get(name)
            byFile[name] = {
              students: students.get(name)?.records ?? 0,
              allocation: lines?.records ?? 0,
              ...(lines?.alreadyAssigned != null || lines?.newAssignments != null
                ? {
                    alreadyAssigned: lines?.alreadyAssigned ?? 0,
                    newAssignments: lines?.newAssignments ?? 0,
                  }
                : {}),
              ...(lines?.awaitingReview ? {awaitingReview: lines.awaitingReview} : {}),
              ...(lines?.unresolvedLines ? {unresolvedLines: lines.unresolvedLines} : {}),
            }
          })
          setCarried({checking: false, byFile})
          // Straight to the parent, which outlives this screen: Review may already be open.
          if (TEACHER_FILE_SUMMARY_ON_REVIEW) {
            onFileCountsRef.current?.(countsFrom(byFile, filesRef.current))
          }
        },
        // Review waits on `checking`: whatever goes wrong, the check ends.
        () => {
          if (!cancelled()) setCarried({checking: false, byFile: {}})
        }
      )
    },
    [dispatch]
  )

  /** Summary line under a file: translated from `counts`, else the server's own message. */
  const summarize = useCallback(
    (counts: DataUploadCounts | null, message: string | null) =>
      dataUploadCountsSummary(intl.formatMessage, counts, 'teachers') ?? message,
    [intl]
  )

  /**
   * Copy for a failed file. A known code (the file's `reason`, or `result.code` of a failed
   * call) gets its translated copy, with the count behind it for a row code
   * (`NAME_NOT_ENGLISH`: "4 names are not written in English letters…"). Otherwise the server's
   * `message`; then the call's HTTP status (413, 504, connection lost); then the generic
   * "could not extract".
   */
  const describeFailure = useCallback(
    (
      item: UploadItem,
      error: unknown,
      extraction?: TeacherExtraction,
      /** The failed source's own `retryable`, when the server sent one. */
      retryableSaid?: boolean
    ): Partial<UploadItem> => {
      const code = extraction?.error ?? dataUploadErrorCode(error)
      const counts = extraction?.counts
      const failed = {
        status: 'error' as const,
        errorKey: ERROR_KEYS.extract,
        skipped: extraction?.skipped ?? [],
        summary: null,
        errorCode: code ?? undefined,
        // A damaged or wrong file fails the same way every time: no "Try again" for it.
        retryable: dataUploadRetryable(code, retryableSaid, error),
      }
      if (code && code in DATA_UPLOAD_ERROR_KEYS) {
        return {
          ...failed,
          errorText: dataUploadErrorMessage(intl.formatMessage, code, {
            config: uploadConfig,
            useCaseKey: 'teachers',
            fileName: item.file.name,
            // One file of several has no `counts` of its own: its left-out rows say how many.
            count:
              (code && counts?.leftOut[code]) ||
              counts?.read ||
              extraction?.skipped.filter((row) => (row.code ?? row.reason) === code).length ||
              undefined,
          }),
        }
      }
      if (extraction?.message) return {...failed, errorText: extraction.message}
      if (!extraction && error !== undefined) {
        return {
          ...failed,
          errorText: dataUploadErrorMessage(intl.formatMessage, error, {
            config: uploadConfig,
            useCaseKey: 'teachers',
            fileName: item.file.name,
          }),
        }
      }
      return {...failed, errorText: undefined}
    },
    [intl, uploadConfig]
  )

  const applyExtraction = useCallback(
    (item: UploadItem, extraction: TeacherExtraction) => {
      // Nothing was read: the workbook holds several lists of people and says of none of them
      // whose it is. Ask, then send the same file again with the answers.
      if (extraction.assignmentQuestion) {
        // Asked from the file's row ("Answer"), never in a dialog that opens by itself.
        patchFile(item.id, {status: 'needs-assignment', rows: [], summary: null})
        setSheetQuestion(extraction.assignmentQuestion)
        return
      }
      if (extraction.assignedTo) {
        // Sent to another step on the user's answer: an info line, never an error. The prefill
        // status under it ("5 students will be filled in on the Students step") still shows.
        patchFile(item.id, {
          status: 'assigned',
          assignedTo: extraction.assignedTo,
          assignedBy: extraction.assignedBy,
          rows: [],
          summary: null,
        })
        return
      }
      if (extraction.error) {
        patchFile(item.id, describeFailure(item, undefined, extraction))
        return
      }
      if (extraction.rows.length === 0) {
        patchFile(item.id, {
          status: 'error',
          errorKey: ERROR_KEYS.noNames,
          errorText: extraction.message ?? undefined,
          skipped: extraction.skipped,
        })
        return
      }
      patchFile(item.id, {
        status: 'ready',
        rows: extraction.rows,
        summary: summarize(extraction.counts, extraction.message),
        skipped: extraction.skipped,
      })
    },
    [describeFailure, patchFile, summarize]
  )

  /**
   * One server read of all the files: the merged teachers, one question for every file that
   * needs one, and a status line per file from `sources[]` (its own `records`, `status`,
   * `needsReview`, `partial` and `assignedTo`).
   *
   * A "whose list is this?" question no longer stops the others: the files it is about wait for
   * their answer (asked from their row, never in a dialog that opens by itself), and every other
   * file's teachers are shown as read (QA 13 Oct, #365).
   */
  const applyServerRead = useCallback(
    (items: UploadItem[], extraction: TeacherExtraction) => {
      const question = extraction.assignmentQuestion
      const askedNames = new Set((question?.sheets ?? []).map((sheet) => sheet.fileName ?? ''))
      const isAsked = (item: UploadItem) =>
        Boolean(question) && (askedNames.has('') || askedNames.has(item.file.name))
      // A "question" about sheets with no header and no rows is an empty file: say so on its row
      // instead of asking whose list it is (older servers; newer ones answer NO_RELEVANT_DATA).
      const emptyQuestion =
        Boolean(question) &&
        question!.sheets.every((sheet) => !sheet.headers?.length && !sheet.sample?.length)
      if (question) {
        items.filter(isAsked).forEach((item) =>
          patchFile(
            item.id,
            emptyQuestion
              ? describeFailure(item, 'NO_RELEVANT_DATA')
              : {status: 'needs-assignment', rows: [], summary: null}
          )
        )
        setSheetQuestion(emptyQuestion ? null : question)
        if (extraction.rows.length === 0 && !emptyQuestion) {
          // An older server read nothing next to its question: the other files wait for it too.
          items
            .filter((item) => !isAsked(item))
            .forEach((item) => patchFile(item.id, {status: 'extracting'}))
          setServerRows([])
          return
        }
      } else {
        setSheetQuestion(null)
      }
      const readable = items.filter((item) => !isAsked(item))
      setServerRows(extraction.rows)
      // Asked now that the rows are read: columns it could not place for sure, sheets whose
      // branch it could not tell, and subjects written under a name the school does not use.
      // Skipped ones are not asked again either: a skipped column is answered `ignore`, and
      // skipped branches and subjects are remembered here.
      const columns = withoutAnsweredColumns(extraction.columnQuestions, columnAnswersRef.current)
      const branches = withoutAnsweredBranches(
        extraction.branchQuestions,
        branchAnswersRef.current
      ).filter((question) => !skippedBranchKeysRef.current.has(question.key))
      // Mappings confirmed earlier are not asked again; when the dialog opens for something
      // else they show as done.
      const openSubjects = withoutConfirmedSubjects(
        extraction.subjectMappings ?? null,
        new Set([...subjectAnswersRef.current.keys(), ...skippedSubjectsRef.current])
      )
      const subjects =
        (openSubjects?.suggestions.length ?? 0) > 0
          ? withConfirmedSubjectsApplied(openSubjects, subjectAnswersRef.current)
          : null
      // "Which classes?" only for what the file genuinely leaves open (12 Oct review, point 1):
      // a subject whose classes the file itself names (`fromFile`) is answered with them here,
      // never asked; only the rest reaches the dialog.
      const open = teachersOnlyRef.current
        ? []
        : withoutAnsweredAllocations(extraction.allocationQuestions, allocationAnswersRef.current)
      // Add Teachers gets every line that writes a class: the clear ones come `fromFile` with
      // their classes (the ones the file names, `picked`, when it says), answered silently here
      // and sent with the rest to `onboarding/prefill`, which then places exactly these.
      const fromFile = open.flatMap((entry) =>
        entry.subjects
          .filter(
            (subject) =>
              subject.subject && subject.fromFile && !subject.pending && subject.options.length > 0
          )
          .map((subject) => {
            const offered = subject.options.map((option) => option.classId)
            const named = subject.picked
              ? offered.filter((classId) => subject.picked!.includes(classId))
              : []
            return {
              teacher: entry.teacher,
              employeeId: entry.employeeId,
              subject: subject.subject as string,
              classIds: named.length > 0 ? named : offered,
            }
          })
      )
      if (fromFile.length > 0) {
        allocationAnswersRef.current = [...allocationAnswersRef.current, ...fromFile]
        onAllocationAnswers?.(allocationAnswersRef.current)
      }
      const allocations = withoutAnsweredAllocations(open, fromFile)
      // Lines confirmed with no classes (`pending`) stay on screen as "N need review", for the
      // Review step too; on their own they never open the dialog (it would come back after
      // every Confirm).
      onPendingAllocations?.(allocationLineCounts(allocations).review > 0 ? allocations : [])
      const opens = Boolean(
        columns || branches.length > 0 || subjects || askableAllocations(allocations).length > 0
      )
      const asked = opens || allocations.length > 0
      setQuestions(asked ? {columns, branches, subjects, allocations} : null)
      setQuestionsOpen(opens)
      /** The rows of this read that came from `item` and need a look (older servers' count). */
      const needsCheckOf = (item: UploadItem) =>
        extraction.rows.filter(
          (row) =>
            row._needsReview &&
            (!row._sourceFiles || readable.length === 1 || row._sourceFiles.includes(item.file.name))
        ).length
      /** What the read reported that is only information (every line skipped): said once. */
      const infoTexts = new Set<string>()

      readable.forEach((item, index) => {
        const source =
          extraction.sources.find((entry) => entry.fileName === item.file.name) ??
          (extraction.sources.length === readable.length ? extraction.sources[index] : undefined)
        // The rows the file dropped, with why: the report's list (one file), else `leftOut`.
        const skipped =
          readable.length === 1 && extraction.skipped.length > 0
            ? extraction.skipped
            : (source?.leftOut ?? [])
        const assignedTo =
          source?.assignedTo ?? (readable.length === 1 ? extraction.assignedTo : undefined)
        if (assignedTo) {
          patchFile(item.id, {
            status: 'assigned',
            assignedTo,
            assignedBy: source?.assignedTo ? source.assignedBy : extraction.assignedBy,
            rows: [],
            summary: null,
          })
          return
        }
        // Every line skipped on purpose ("Which classes?" answered with none) is information,
        // not a failure: said once, in the server's words, and the file does not turn red.
        const reason = source?.reason ?? extraction.error
        const info = Boolean(reason) && dataUploadErrorIsInfo(reason)
        if (info) {
          infoTexts.add(
            source?.message ??
              extraction.message ??
              dataUploadErrorMessage(intl.formatMessage, reason, {useCaseKey: 'teachers'})
          )
        }
        // Every row left out is an error too (`status: 'error'` + `reason`, `message`): the
        // reason and the dropped rows show under the file, single file or several alike.
        const failedWhole = readable.length === 1 && Boolean(extraction.error) && !source
        if (!info && (source?.status === 'error' || failedWhole || (!source && extraction.error))) {
          patchFile(
            item.id,
            describeFailure(
              item,
              undefined,
              {
                ...extraction,
                error: source?.reason ?? extraction.error ?? 'PARSE_FAILED',
                message: source?.message ?? extraction.message,
                counts: source && readable.length > 1 ? null : extraction.counts,
                skipped,
              },
              source?.retryable
            )
          )
          return
        }
        // `records` is what the file kept (rows read on older servers).
        const found =
          source?.records ??
          source?.rows ??
          (readable.length === 1 ? extraction.rows.length : 0)
        // Older servers (and a file that genuinely holds nothing) send no owner: when the other
        // files did give teachers, this one is just empty, not a failure.
        if (
          found === 0 &&
          (info || (source?.status === 'no_relevant_data' && extraction.rows.length > 0))
        ) {
          patchFile(item.id, {status: 'ready', found: 0, noTeachers: true, summary: null, skipped})
          return
        }
        // A green tick only for `status: 'ok'` with records kept (no status: an older server).
        if (found === 0 || (!info && source?.status && source.status !== 'ok')) {
          patchFile(item.id, {
            status: 'error',
            errorKey: ERROR_KEYS.noNames,
            errorText: source?.message ?? (readable.length === 1 ? extraction.message : null) ?? undefined,
            errorCode: 'NO_RELEVANT_DATA',
            retryable: false,
            skipped,
          })
          return
        }
        patchFile(item.id, {
          status: 'ready',
          found,
          // The server's per-file count of rows to check; the rows' own flags on older servers.
          needsCheck: source?.needsReview ?? needsCheckOf(item),
          // Read with rows left out: `partial` from the server, else the left-out rows themselves.
          partial: source?.partial ?? skipped.length > 0,
          leftOutCount: source?.leftOutCount ?? skipped.length,
          summary: readable.length === 1 ? summarize(extraction.counts, extraction.message) : null,
          skipped,
        })
      })
      infoTexts.forEach((text) => toast.info(text))
    },
    [describeFailure, intl, onAllocationAnswers, onPendingAllocations, patchFile, summarize]
  )

  /**
   * Upload each file in pieces, then read them in one `extract-teachers` request (`uploadIds`):
   * the server merges a teacher named in two files into one row, which one request per file
   * could not. When the server says the files also hold students or allocations
   * (`prefill.useCaseKeys`), the same ids go to `onboarding/prefill` for those steps.
   *
   * What a read leaves alone (QA 13 Oct, #363/#364/#368): a file already read keeps its result
   * while the list is read again; a file that failed keeps its error (it is sent again only when
   * its own "Try again" asks, `retryIds`); a file waiting for its "whose list?" answer waits
   * (sent again once answered, `includeWaiting`). `silent` (a file removed) rebuilds the merged
   * list in the background: the rows already shown stay, and only Review waits for it.
   */
  const readOnServer = useCallback(
    async (
      items: UploadItem[],
      opts: {retryIds?: ReadonlySet<string>; includeWaiting?: boolean; silent?: boolean} = {}
    ) => {
      readAbortRef.current?.abort()
      prefillAbortRef.current?.abort()
      // The prefill drafts of the previous read are superseded (a file was added or removed, or
      // an answer changed): delete them, so the Allocation and Students steps can only pick up
      // this read's — never assignments from a file that is no longer on the list.
      const seq = ++readSeqRef.current
      discardPrefillDraftIds(dispatch, prefillDraftIdsRef.current)
      prefillDraftIdsRef.current = []
      setCarried(null)
      // Questions left open come back with this read (answered and skipped ones do not).
      setQuestions(null)
      setQuestionsOpen(false)
      setSheetQuestionOpen(false)
      const included = items.filter((item) => {
        // Read from its carried draft (`readCarried`): never uploaded.
        if (item.carriedDraftId) return false
        if (item.status === 'error') return Boolean(opts.retryIds?.has(item.id))
        if (item.status === 'needs-assignment') return Boolean(opts.includeWaiting)
        return true
      })
      if (included.length === 0) {
        readAbortRef.current = null
        setReadInFlight(false)
        setRefreshInFlight(false)
        setServerRows([])
        setSheetQuestion(null)
        return
      }
      const controller = new AbortController()
      readAbortRef.current = controller
      const aborted = () => controller.signal.aborted
      if (opts.silent) setRefreshInFlight(true)
      else setReadInFlight(true)
      const settle = () => {
        setReadInFlight(false)
        setRefreshInFlight(false)
      }
      // A file already read keeps its result on screen while the list is read again (its upload
      // is cached, so it is not sent twice): adding a file must not reset the others' progress.
      const keepsResult = (item: UploadItem) =>
        item.status === 'ready' || item.status === 'assigned'
      // Every answer so far rides along, since the server remembers none.
      const answers = sheetAnswersRef.current
      const columnAnswers = columnAnswersRef.current
      const branchAnswers = branchAnswersRef.current
      // The files of the selection already uploaded: a new one is checked against them, so one
      // file too many (or over the total) is refused on its own before any piece is sent.
      const acceptedIds = new Map(items.map((item) => [item.id, uploadedIdOf(item.file)]))
      const upload = (item: UploadItem) =>
        uploadFileInPieces(dispatch, item.file, {
          useCaseKey: 'teachers',
          signal: controller.signal,
          onProgress: (progress) => patchFile(item.id, {progress}),
          selectedUploadIds: items
            .filter((other) => other.id !== item.id && other.status !== 'error')
            .map((other) => acceptedIds.get(other.id))
            .filter((id): id is string => Boolean(id)),
        })
      /** Files the server refused for the selection as a whole: taken off, with the reason. */
      const refused: Array<{item: UploadItem; text: string}> = []

      if (!opts.silent) {
        included.forEach((item) => {
          if (keepsResult(item)) return
          patchFile(item.id, {
            status: 'uploading',
            progress: 0,
            rows: [],
            found: undefined,
            needsCheck: undefined,
            partial: undefined,
            leftOutCount: undefined,
            assignedTo: undefined,
            summary: null,
            skipped: [],
            errorKey: undefined,
            errorText: undefined,
            errorCode: undefined,
            retryable: undefined,
          })
        })
      }
      const uploaded = await Promise.all(
        included.map(async (item) => {
          try {
            const uploadId = await upload(item)
            // A file still being added when another is removed moves on to reading as usual;
            // the files already read keep their result (`keepsResult`).
            if (!aborted() && !keepsResult(item)) {
              patchFile(item.id, {status: 'extracting'})
            }
            return {item, uploadId}
          } catch (error) {
            if (isAbortError(error) || aborted()) return null
            // A file removed: the files left were read already and keep their result even if
            // the background rebuild cannot send one again (QA 13 Oct, #363).
            if (opts.silent && keepsResult(item)) return null
            const code = dataUploadErrorCode(error)
            if (code === 'TOO_MANY_FILES' || code === 'TOTAL_TOO_LARGE') {
              refused.push({
                item,
                text: dataUploadErrorMessage(intl.formatMessage, error, {
                  config: uploadConfig,
                  useCaseKey: 'teachers',
                  fileName: item.file.name,
                }),
              })
              return null
            }
            patchFile(item.id, describeFailure(item, error))
            return null
          }
        })
      )
      if (aborted()) return
      if (refused.length > 0) {
        // Only those files go; the rest of the selection is read as usual.
        const gone = new Set(refused.map((entry) => entry.item.id))
        setFiles((prev) => prev.filter((f) => !gone.has(f.id)))
        setRejectedFiles((prev) => [
          ...prev,
          ...refused.map((entry) => ({name: entry.item.file.name, text: entry.text})),
        ])
      }
      const sent = uploaded.filter(
        (entry): entry is {item: UploadItem; uploadId: string} => entry !== null
      )
      if (sent.length === 0) {
        settle()
        return
      }
      const sentItems = sent.map((entry) => entry.item)

      const post = (uploadIds: string[]) => {
        const form = new FormData()
        form.append('uploadIds', JSON.stringify(uploadIds))
        appendSheetAssignments(form, answers)
        appendColumnAssignments(form, columnAnswers)
        appendBranchAssignments(form, branchAnswers)
        appendAllocationAssignments(form, allocationAnswersRef.current)
        return dispatchApi(dispatch, (ok, fail) => extractOnboardingTeachers(form, ok, fail))
      }
      let uploadIds = sent.map((entry) => entry.uploadId)
      try {
        let response: unknown
        try {
          response = await post(uploadIds)
        } catch (error) {
          // An expired or half-sent upload is sent again, once, like `withUploadedFile` does.
          const code = dataUploadErrorCode(error)
          if (aborted() || (code !== 'UPLOAD_NOT_FOUND' && code !== 'UPLOAD_INCOMPLETE'))
            throw error
          sentItems.forEach((item) => forgetUploadedFile(item.file))
          uploadIds = await Promise.all(sentItems.map(upload))
          response = await post(uploadIds)
        }
        if (aborted()) return
        settle()
        const extraction = normalizeExtractionResponse(
          response,
          sentItems.length === 1 ? sentItems[0].file.name : ''
        )
        applyServerRead(sentItems, extraction)
        // What else the files hold, for the Students and Allocation steps: only when the server
        // says there is some (`prefill.useCaseKeys`; [] for a names-only list, so no prefill and
        // no "Checking…" wait). An older server does not say: the prefill is asked as before.
        const keys = extraction.prefillUseCaseKeys
        if (keys && keys.length === 0) return
        // The drafts this read's prefill makes are kept (to discard when superseded); a prefill
        // answering after a newer read started is already stale, so its drafts go at once.
        const allocationAnswers = allocationAnswersRef.current
        // The same body as the read, so the Students and Allocation drafts read it the same way.
        const prefillBody = {
          uploadIds,
          ...(keys && keys.length > 0 ? {useCaseKeys: keys} : {}),
          ...(Object.keys(answers).length > 0 ? {sheetAssignments: answers} : {}),
          ...(Object.keys(columnAnswers).length > 0 ? {columnAssignments: columnAnswers} : {}),
          ...(Object.keys(branchAnswers).length > 0 ? {branchAssignments: branchAnswers} : {}),
          ...(allocationAnswers.length > 0 ? {allocationAssignments: allocationAnswers} : {}),
        }
        const onPrefill = (res: any) => {
          const ids = prefillDraftIdsOf(res)
          if (seq !== readSeqRef.current) {
            discardPrefillDraftIds(dispatch, ids)
            return
          }
          prefillDraftIdsRef.current = ids
          onPrefillRequest?.({body: prefillBody, draftIds: ids})
          watchCarried(res)
        }
        // "Checking the file for students and allocations" with a spinner under the headline until
        // the count lands; Review waits for it.
        setCarried({checking: true, byFile: {}})
        dispatch(
          prefillOnboardingFromFile(
            prefillBody,
            onPrefill,
            // A 202 the saga does not count as success still carries the drafts.
            onPrefill
          ) as never
        )
      } catch (error) {
        if (isAbortError(error) || aborted()) return
        settle()
        // A failed call can still carry the result (reason, message, counts).
        const extraction = normalizeExtractionResponse(
          error,
          sentItems.length === 1 ? sentItems[0].file.name : ''
        )
        // Every line skipped on purpose: information in the server's words, not an error. The
        // files already read keep their result; the others hold no teachers, a neutral line.
        if (dataUploadErrorIsInfo(error)) {
          toast.info(
            dataUploadErrorMessage(intl.formatMessage, error, {
              config: uploadConfig,
              useCaseKey: 'teachers',
            })
          )
          sentItems
            .filter((item) => !keepsResult(item))
            .forEach((item) =>
              patchFile(item.id, {
                status: 'ready',
                found: 0,
                noTeachers: true,
                summary: null,
                skipped: extraction.skipped,
              })
            )
          return
        }
        // The background rebuild after a removal failed: the names already shown (the removed
        // file's taken out) stay, and no file already read turns red.
        sentItems
          .filter((item) => !(opts.silent && keepsResult(item)))
          .forEach((item) =>
          patchFile(
            item.id,
            describeFailure(item, error, extraction.error ? extraction : undefined)
          )
        )
      }
    },
    [
      applyServerRead,
      describeFailure,
      dispatch,
      intl,
      onPrefillRequest,
      patchFile,
      uploadConfig,
      watchCarried,
    ]
  )
  /** Local UI work only (`VITE_MOCK_ONBOARDING_TEACHER_EXTRACTION`): a fake answer, no upload. */
  const extractWithMock = useCallback(
    (item: UploadItem) => {
      patchFile(item.id, {status: 'extracting'})
      window.setTimeout(
        () =>
          applyExtraction(
            item,
            normalizeExtractionResponse(buildMockExtraction(item.file.name), item.file.name)
          ),
        900
      )
    },
    [applyExtraction, patchFile]
  )

  /** Excel-only servers: the sheet is parsed here, with the server's row rules. */
  const parseInBrowser = useCallback(
    async (item: UploadItem) => {
      const ext = fileExtension(item.file.name)
      let sheetRows: string[][]
      try {
        sheetRows =
          ext === 'csv' ? parseCsvRows(await item.file.text()) : await excelFileToRows(item.file)
      } catch {
        patchFile(item.id, {status: 'error', errorKey: ERROR_KEYS.read})
        return false
      }
      const parsed = parseTeachersRows(sheetRows, item.file.name)
      if (parsed.error) return true
      if (parsed.rows.length === 0) {
        // Every row broke a rule: say which one hit most, as the server does in `reason`.
        const {leftOut} = browserCounts(0, parsed.skipped)
        const [topCode, topCount] = Object.entries(leftOut).sort((a, b) => b[1] - a[1])[0] ?? []
        patchFile(item.id, {
          status: 'error',
          errorKey: ERROR_KEYS.noNames,
          skipped: parsed.skipped,
          errorText: topCode
            ? dataUploadErrorMessage(intl.formatMessage, topCode, {count: topCount})
            : undefined,
        })
        return false
      }
      patchFile(item.id, {
        status: 'ready',
        rows: parsed.rows,
        summary:
          parsed.skipped.length > 0
            ? summarize(browserCounts(parsed.rows.length, parsed.skipped), null)
            : null,
        skipped: parsed.skipped,
      })
      return false
    },
    [intl, patchFile, summarize]
  )

  /**
   * A Teacher Allocation file's teachers (`carried.teachers`): the draft as our template workbook
   * (`drafts/:id/template`), parsed here like a hand-filled template — nothing is uploaded again.
   */
  const readCarried = useCallback(
    async (item: UploadItem) => {
      const draftId = item.carriedDraftId
      if (!draftId) return
      patchFile(item.id, {status: 'parsing', errorKey: undefined, errorText: undefined, skipped: []})
      try {
        const res: any = await dispatchApi(dispatch, (ok, fail) =>
          getDataUploadDraftTemplate(draftId, {flavour: 'onboarding'}, ok, fail)
        )
        const template = normalizeDataUploadConversion(res?.result ?? res)?.template
        if (!template) {
          patchFile(item.id, {status: 'error', errorKey: ERROR_KEYS.noNames, retryable: false})
          return
        }
        // Our own template always has its header: `true` (no header) would mean a broken draft.
        const unstructured = await parseInBrowser({...item, file: dataUploadTemplateToFile(template)})
        if (unstructured) patchFile(item.id, {status: 'error', errorKey: ERROR_KEYS.read})
      } catch (error) {
        patchFile(item.id, describeFailure(item, error))
      }
    },
    [describeFailure, dispatch, parseInBrowser, patchFile]
  )

  // A carried file handed over: one line for it, read from its draft (once per draft).
  const carriedDraftId = carriedTeachers?.draftId
  useEffect(() => {
    if (!carriedTeachers) return
    onCarriedTeachersTaken?.(carriedTeachers.draftId)
    const item: UploadItem = {
      id: nextUploadId(),
      file: carriedTeachers.file,
      kind: 'spreadsheet',
      status: 'parsing',
      rows: [],
      carriedDraftId: carriedTeachers.draftId,
    }
    setFiles((prev) =>
      prev.some((entry) => entry.carriedDraftId === item.carriedDraftId) ? prev : [...prev, item]
    )
    void readCarried(item)
    // Once per draft, not per new callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carriedDraftId])

  /** A file read in the browser or by the local mock; server files go through `readOnServer`. */
  const processFile = useCallback(
    async (item: UploadItem) => {
      if (item.kind === 'spreadsheet') {
        // `true`: no recognisable header, so the sheet can't be trusted as parsed.
        const unstructured = await parseInBrowser(item)
        if (!unstructured) return
        if (!extractionEnabled) {
          patchFile(item.id, {status: 'error', errorKey: ERROR_KEYS.noHeader})
          return
        }
      }
      extractWithMock(item)
    },
    [extractWithMock, extractionEnabled, parseInBrowser, patchFile]
  )

  const addFiles = useCallback(
    (incoming: File[]) => {
      if (incoming.length === 0) return
      // Single-file mode: a new file replaces whatever is queued instead of being refused
      // as "too many", so the user can just drop the corrected sheet again.
      const {accepted, rejected} = validateTeacherFiles(
        singleFile ? [] : files.map((f) => f.file),
        incoming,
        {extractionEnabled, maxFileBytes, maxFiles, maxTotalBytes}
      )

      // Refused files are listed on the page with the reason for each, right away; the files
      // already accepted (earlier or in this same drop) stay as they are.
      setRejectedFiles(
        rejected.map(({file, reason}) => ({
          name: file.name,
          text:
            reason === 'unsupported' && extractionEnabled
              ? // Says which formats work, and asks for a re-save when it is a legacy .xls / .doc.
                dataUploadErrorMessage(intl.formatMessage, 'FILE_TYPE_NOT_ALLOWED', {
                  fileName: file.name,
                })
              : intl.formatMessage(
                  {id: REJECTION_KEYS[reason]},
                  {
                    limit:
                      reason === 'too-many'
                        ? maxFiles
                        : formatBytes(reason === 'total-too-large' ? maxTotalBytes : maxFileBytes),
                  }
                ),
        }))
      )

      const items: UploadItem[] = accepted.map((file) => ({
        id: nextUploadId(),
        file,
        kind: classifyTeacherFile(file, extractionEnabled) ?? 'document',
        status: 'parsing',
        rows: [],
      }))
      if (items.length === 0) return
      const next = singleFile ? items : [...files, ...items]
      setFiles(next)
      if (serverRead) {
        // All of them in one request, so a teacher in two files is merged.
        void readOnServer(next)
        return
      }
      items.forEach((item) => {
        void processFile(item)
      })
    },
    [
      extractionEnabled,
      files,
      intl,
      maxFileBytes,
      maxFiles,
      maxTotalBytes,
      singleFile,
      serverRead,
      readOnServer,
      processFile,
    ]
  )

  const removeFile = (id: string) => {
    const removed = files.find((f) => f.id === id)
    const remaining = files.filter((f) => f.id !== id)
    setFiles(remaining)
    // A carried file is not part of the files' read: its draft is closed, the others stay as read.
    if (removed?.carriedDraftId) {
      onCarriedDraftDone?.(removed.carriedDraftId)
      return
    }
    // A removed file is gone for good: its upload id is not reused if it is added again, and the
    // "which classes?" answers given for the list are dropped (here and on the Allocation step),
    // so no assignment from it can be merged back in. What is left is asked again if needed.
    if (removed) forgetUploadedFile(removed.file)
    allocationAnswersRef.current = []
    teachersOnlyRef.current = false
    onAllocationAnswers?.([])
    onPendingAllocations?.([])
    if (remaining.length === 0) {
      sheetAnswersRef.current = {}
      columnAnswersRef.current = {}
      branchAnswersRef.current = {}
      skippedBranchKeysRef.current = new Set()
      skippedSubjectsRef.current = new Set()
      setBranchAnswered({questions: [], answers: {}})
    }
    if (!serverRead) return
    // Its teachers go at once (those read only from it), with no spinner and no "Reading your
    // files" (QA 13 Oct, #363); the merged list is then rebuilt in the background. With no file
    // left this still runs: it deletes the last read's prefill drafts.
    const remainingNames = new Set(remaining.map((f) => f.file.name))
    setServerRows((rows) =>
      rows.filter(
        (row) => !row._sourceFiles || row._sourceFiles.some((name) => remainingNames.has(name))
      )
    )
    void readOnServer(remaining, {silent: true})
  }

  /**
   * Read a failed file again, that file only (fresh upload): the others keep their results and
   * any other failed file keeps its error (QA 13 Oct, #364).
   */
  const retryFile = (item: UploadItem) => {
    if (item.carriedDraftId) {
      void readCarried(item)
      return
    }
    if (serverRead) {
      forgetUploadedFile(item.file)
      void readOnServer(files, {retryIds: new Set([item.id])})
      return
    }
    patchFile(item.id, {status: 'parsing', errorKey: undefined, errorText: undefined, skipped: []})
    void processFile({...item, status: 'parsing'})
  }

  // --- Review ---------------------------------------------------------------------

  const reviewRows = () => {
    const fromFiles = files.flatMap((f) => (f.status === 'ready' ? f.rows : []))
    return [...namesToTeacherRows(names, 'manual_entry'), ...fromFiles, ...serverRows]
  }

  /** Review was clicked while `reviewWaitsOn`: it opens once that is done. */
  const [reviewQueued, setReviewQueued] = useState(false)

  /** To Review: the rows, and the carried drafts whose teachers went with them are closed. */
  const goToReview = () => {
    files
      .filter((item) => item.carriedDraftId && item.status === 'ready')
      .forEach((item) => onCarriedDraftDone?.(item.carriedDraftId!))
    onReview(reviewRows(), TEACHER_FILE_SUMMARY_ON_REVIEW ? carriedCounts() : null)
  }

  const handleReview = () => {
    if (!canReview) return
    if (reviewWaitsOn) {
      setReviewQueued(true)
      return
    }
    goToReview()
  }

  useEffect(() => {
    if (!reviewQueued) return
    // Something now needs the user (a file being read, a question): the click is dropped.
    if (!canReview) {
      setReviewQueued(false)
      return
    }
    if (reviewWaitsOn) return
    setReviewQueued(false)
    goToReview()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when the wait ends
  }, [reviewQueued, canReview, reviewWaitsOn])

  useEffect(() => {
    if (!pendingRowsRef) return
    pendingRowsRef.current = () => {
      const rows = reviewRows()
      if (rows.length === 0) return null
      return {rows, counts: TEACHER_FILE_SUMMARY_ON_REVIEW ? carriedCounts() : null}
    }
    return () => {
      pendingRowsRef.current = null
    }
  })

  /**
   * The students and allocation lines of all the files, counted as the summary does: an
   * allocation only from a file we found teachers in (an allocation names a teacher). Null when
   * the files were not checked, or the check has not answered.
   */
  const carriedCounts = (): TeacherFileCounts | null =>
    !carried || carried.checking ? null : countsFrom(carried.byFile, files)
  function countsFrom(
    byFile: Record<string, TeacherFileExtra>,
    list: UploadItem[]
  ): TeacherFileCounts {
    return list.reduce(
      (sum, item) => {
        const extra = byFile[item.file.name.trim().toLowerCase()]
        if (!extra) return sum
        const teachers = item.status === 'ready' ? (item.found ?? item.rows.length) : 0
        const next: TeacherFileCounts = {
          students: sum.students + extra.students,
          allocation: sum.allocation + (teachers > 0 ? extra.allocation : 0),
        }
        // A re-upload: how many of those cells already have their teacher, and how many are new.
        if (teachers > 0 && (extra.alreadyAssigned != null || extra.newAssignments != null)) {
          next.alreadyAssigned = (sum.alreadyAssigned ?? 0) + (extra.alreadyAssigned ?? 0)
          next.newAssignments = (sum.newAssignments ?? 0) + (extra.newAssignments ?? 0)
        } else if (sum.alreadyAssigned != null) {
          next.alreadyAssigned = sum.alreadyAssigned
          next.newAssignments = sum.newAssignments
        }
        // Proposals and unresolved lines, from the files we found teachers in, as `allocation`.
        const awaitingReview = (sum.awaitingReview ?? 0) + (teachers > 0 ? (extra.awaitingReview ?? 0) : 0)
        const unresolvedLines =
          (sum.unresolvedLines ?? 0) + (teachers > 0 ? (extra.unresolvedLines ?? 0) : 0)
        if (awaitingReview > 0) next.awaitingReview = awaitingReview
        if (unresolvedLines > 0) next.unresolvedLines = unresolvedLines
        return next
      },
      {students: 0, allocation: 0} as TeacherFileCounts
    )
  }

  // A file removed is not a read (QA 13 Oct, #363): no "Reading your files", just a short
  // "updating" while the merged list is rebuilt in the background.
  const reviewHintKey =
    refreshInFlight && !isBusy
      ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REVIEW_HINT_UPDATING'
      : isBusy
        ? files.length > 0
          ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REVIEW_HINT_READING'
          : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REVIEW_HINT_BUSY'
        : awaitingAssignment
          ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REVIEW_HINT_ANSWER'
          : carriedChecking
            ? files.length > 1
              ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.CARRIED_CHECKING_FILES'
              : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.CARRIED_CHECKING'
          : canReview
            ? fileRowCount > 0
              ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REVIEW_HINT_FILES_READY'
              : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REVIEW_HINT_ENABLED'
            : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REVIEW_HINT_DISABLED'

  /** A file read with some of its rows left out: usable, but it needs a look. */
  const isPartlyRead = (item: UploadItem) =>
    item.status === 'ready' &&
    !item.noTeachers &&
    (item.partial ?? (item.skipped?.length ?? 0) > 0)

  /** Teachers a file gave: its own count on a server read, its rows when read in the browser. */
  const teachersIn = (item: UploadItem) =>
    item.status === 'ready' && !item.noTeachers ? (item.found ?? item.rows.length) : 0

  /**
   * A file the server moved to another step itself: a Students list says it is ready there.
   * `step` is that step's title.
   */
  const movedText = (item: UploadItem, step: string) =>
    item.assignedTo === 'students'
      ? intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.MOVED_TO_STUDENTS'})
      : intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.MOVED_TO_STEP'}, {step})

  /**
   * What a file's row in the status list says, and the state of its status icon. A green tick:
   * the file was read and is ready — its names to review here (B3 of the 11 Oct review), or its
   * list handed to the step it belongs to.
   */
  const fileState = (
    item: UploadItem
  ): {tone: UploaderFileState; text: string} => {
    const t = (id: string, values?: Record<string, string | number>) =>
      intl.formatMessage({id: `ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.${id}`}, values)
    switch (item.status) {
      case 'uploading':
        return {
          tone: 'upload',
          text: t('FILE_STATUS_UPLOADING', {percent: Math.round((item.progress ?? 0) * 100)}),
        }
      case 'parsing':
        return {tone: 'busy', text: t('FILE_STATUS_READING_NAMES')}
      case 'extracting':
        // While "whose list is this?" is open, the files it is not about wait for the answer.
        return sheetQuestion
          ? {tone: 'neutral', text: t('FILE_STATUS_WAITING')}
          : {tone: 'busy', text: t('FILE_STATUS_READING_NAMES')}
      case 'error':
        return {
          tone: 'error',
          text: item.errorText ?? intl.formatMessage({id: item.errorKey ?? ERROR_KEYS.extract}),
        }
      case 'needs-assignment':
        return {tone: 'question', text: intl.formatMessage({id: 'DATA_UPLOAD.SHEETS.FILE_STATUS'})}
      case 'assigned': {
        if (item.assignedTo === 'ignore') return {tone: 'neutral', text: t('FILE_NOT_READ')}
        const step = STEP_TITLE_KEYS[item.assignedTo ?? '']
          ? intl.formatMessage({id: STEP_TITLE_KEYS[item.assignedTo!]})
          : (item.assignedTo ?? '')
        // Read and handed to its step: done, a tick (not the "–" of a file left unread).
        return {
          tone: 'done',
          text: item.assignedBy === 'file' ? movedText(item, step) : t('FILE_SENT_TO_STEP', {step}),
        }
      }
      case 'ready': {
        if (item.noTeachers) return {tone: 'neutral', text: t('FILE_STATUS_NO_TEACHERS')}
        const leftOut = isPartlyRead(item) ? (item.leftOutCount ?? item.skipped?.length ?? 0) : 0
        // Rows left out: usable, but never the green "all good" tick (QA 13 Oct, #371) — an
        // amber row that says why. Names to check are not counted here: Review Teachers shows
        // them ("To check", "Please verify"), where they can be fixed.
        return {
          tone: leftOut > 0 ? 'warning' : 'done',
          text:
            leftOut > 0
              ? t('FILE_PARTIAL', {count: teachersIn(item), left: leftOut})
              : t('FILE_READY_NAMES', {count: teachersIn(item)}),
        }
      }
      default:
        return {tone: 'neutral', text: ''}
    }
  }

  /**
   * One combined summary under the files: what each file held and where it goes — its teachers
   * here, its students on the Students step, its allocations on the Teacher Allocation step.
   * The later steps' counts come from the one prefill of all the files (`carried.byFile`).
   */
  const renderSummary = () => {
    // Nothing is read until "whose list is this?" is answered; a count from the prefill sent
    // before the answer would only be half the story, so the summary waits for the re-read.
    if (sheetQuestion || awaitingAssignment) return null
    const lines = files.flatMap((item) => {
      const extra = carried?.byFile[item.file.name.trim().toLowerCase()]
      const teachers = item.status === 'ready' ? (item.found ?? item.rows.length) : 0
      const needsCheck =
        item.status === 'ready'
          ? (item.needsCheck ?? item.rows.filter((r) => r._needsReview).length)
          : 0
      // Each thing the file held is a badge, coloured by where it goes.
      const badges: Array<{text: string; tone: string}> = []
      if (teachers > 0) {
        badges.push({
          tone: 'success',
          text: intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_FOUND'},
            {count: teachers}
          ),
        })
      }
      if (needsCheck > 0) {
        badges.push({
          tone: 'warning',
          text: intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_NEEDS_CHECK'},
            {count: needsCheck}
          ),
        })
      }
      if (extra && extra.students > 0) {
        badges.push({
          tone: 'primary',
          text: intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.SUMMARY_STUDENTS'},
            {count: extra.students}
          ),
        })
      }
      // An allocation names a teacher, so a file we found no teacher in cannot have any: the
      // prefill counts a student's class as one, which read as a contradiction.
      if (extra && extra.allocation > 0 && teachers > 0) {
        badges.push({
          tone: 'info',
          text: intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.SUMMARY_ALLOCATION'},
            {count: extra.allocation}
          ),
        })
      }
      if (item.status === 'assigned' && item.assignedTo === 'ignore') {
        badges.push({
          tone: 'secondary',
          text: intl.formatMessage({
            id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_NOT_READ',
          }),
        })
      } else if (item.status === 'assigned' && !extra?.students && !extra?.allocation) {
        // Given to another step, before its count is known. The file itself said whose list it
        // is ("moved"), or the user answered the sheet question ("sent").
        const step = STEP_TITLE_KEYS[item.assignedTo ?? '']
          ? intl.formatMessage({id: STEP_TITLE_KEYS[item.assignedTo!]})
          : item.assignedTo
        badges.push({
          tone: 'primary',
          text:
            item.assignedBy === 'file'
              ? movedText(item, String(step ?? ''))
              : intl.formatMessage(
                  {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_SENT_TO_STEP'},
                  {step}
                ),
        })
      } else if (item.status === 'ready' && item.noTeachers) {
        badges.push({
          tone: 'secondary',
          text: intl.formatMessage({
            id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_STATUS_NO_TEACHERS',
          }),
        })
      }
      return badges.length > 0 ? [{item, badges}] : []
    })
    if (lines.length === 0) return null
    return (
      <section
        className='mb-4'
        style={{
          border: '1px solid var(--s4te-card-border)',
          borderRadius: 12,
          background: 'var(--s4te-card-bg)',
          padding: '0.9rem 1rem 0.35rem',
        }}
        aria-labelledby={`${textareaId}-summary-title`}
      >
        <h4
          id={`${textareaId}-summary-title`}
          className='d-flex align-items-center gap-2 mb-2'
          style={{fontSize: '0.92rem', fontWeight: 700, color: 'var(--s4te-text)'}}
        >
          <CheckCircle2 size={16} style={{color: 'var(--s4te-success)'}} aria-hidden />
          {intl.formatMessage({
            id:
              files.length > 1
                ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.SUMMARY_TITLE_FILES'
                : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.SUMMARY_TITLE',
          })}
        </h4>
        <ul className='list-unstyled mb-0'>
          {lines.map(({item, badges}, index) => (
            <li
              key={item.id}
              className='d-flex flex-wrap align-items-center justify-content-between gap-2 py-2'
              style={index > 0 ? {borderTop: '1px solid var(--s4te-card-border)'} : undefined}
            >
              <span className='d-flex align-items-center gap-2' style={{minWidth: 0}}>
                <span className='step4-te-file-icon' style={{flex: '0 0 auto'}}>
                  <FileKindIcon kind={item.kind} size={16} />
                </span>
                <span style={{minWidth: 0}}>
                  <span
                    className='d-block text-truncate fw-semibold'
                    style={{fontSize: '0.86rem', color: 'var(--s4te-text)'}}
                    title={item.file.name}
                  >
                    {item.file.name}
                  </span>
                  {item.summary ? (
                    <span
                      className='d-block'
                      style={{fontSize: '0.76rem', color: 'var(--s4te-muted)'}}
                    >
                      {item.summary}
                    </span>
                  ) : null}
                </span>
              </span>
              <span className='d-flex flex-wrap justify-content-end gap-2'>
                {badges.map((badge) => (
                  <span
                    key={badge.text}
                    className={`badge badge-light-${badge.tone} fw-semibold`}
                    style={{fontSize: '0.74rem', padding: '0.35rem 0.6rem'}}
                  >
                    {badge.text}
                  </span>
                ))}
              </span>
            </li>
          ))}
        </ul>
      </section>
    )
  }

  /**
   * Where the files stand as a whole, for the status panel's headline: still being read, a
   * question to answer, all read, some failed, none gave names. Null with no files.
   */
  const panelState = ((): UploaderHead['tone'] | null => {
    if (files.length === 0) return null
    if (isBusy) return 'busy'
    // A file waiting for its answer is the headline only while no names are ready; otherwise the
    // ready headline stays and says one file still needs an answer.
    if ((sheetQuestion || awaitingAssignment) && fileRowCount === 0) return 'question'
    const failed = files.some((f) => f.status === 'error')
    if (fileRowCount === 0) return failed ? 'failed' : 'empty'
    return failed || files.some(isPartlyRead) ? 'partial' : 'ready'
  })()

  /**
   * The headline count: teachers across all the files once duplicates are combined. A server
   * read merges them itself (one request for every file); browser-read sheets are combined by
   * exact name here. Similar but different names stay apart — Review flags them.
   */
  const uniqueFileTeachers = serverRead
    ? serverRows.length
    : new Set(
        files
          .flatMap((f) => (f.status === 'ready' ? f.rows : []))
          .map((r) => `${r.first_name} ${r.last_name}`.trim().toLowerCase())
          .filter(Boolean)
      ).size

  /**
   * The unique total as last shown, kept while more files are read (QA 13 Oct, #9): adding a
   * file to a ready list must not hide the count; it updates when the new file finishes.
   */
  const [shownUnique, setShownUnique] = useState<number | null>(null)
  useEffect(() => {
    if (files.length === 0) setShownUnique(null)
    else if (panelState === 'ready' || panelState === 'partial') setShownUnique(uniqueFileTeachers)
  }, [files.length, panelState, uniqueFileTeachers])

  /** The uploader's headline: the per-file counts are on the rows, the unique total here. */
  const statusHead = (): UploaderHead | null => {
    if (!panelState) return null
    const t = (id: string, values?: Record<string, string | number>) =>
      intl.formatMessage({id: `ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.${id}`}, values)
    const total = files.length
    const failed = files.filter((f) => f.status === 'error').length
    const partly = files.filter(isPartlyRead).length
    const found = files.reduce((sum, f) => sum + teachersIn(f), 0)
    // A file that also says who teaches what (a timetable photo, an allocation sheet): once its
    // allocation lines are counted, the headline says what it gave, "12 teachers added. 30
    // assignments applied. …" (12 Oct review, point 1). One line per class-subject.
    const assignments = awaitingAssignment ? 0 : (carriedCounts()?.allocation ?? 0)
    // Its students too, for the Students step (subject-wise schools only: the server leaves
    // them out for class-wise ones, which have no such step).
    const students = awaitingAssignment ? 0 : (carriedCounts()?.students ?? 0)
    // A re-uploaded file (AI server): teachers the school has saved already are said as such, and
    // only the new ones count as ready — "28 names are already in your list", "4 new teachers, 24
    // already in your list". Review merges the saved ones onto their rows.
    const inList = serverRead
      ? serverRows.filter((row) => row._existingTeacherId != null).length
      : 0
    const newTeachers = Math.max(0, uniqueFileTeachers - inList)
    const readyTitle =
      inList === 0
        ? t('PANEL_READY_TITLE', {count: uniqueFileTeachers})
        : newTeachers > 0
          ? t('PANEL_NEW_AND_IN_LIST', {count: newTeachers, inList})
          : t('PANEL_ALREADY_IN_LIST', {count: inList})
    // Shown on a line of its own, with a spinner: it is still in progress.
    const pending = carriedChecking ? t(total > 1 ? 'CARRIED_CHECKING_FILES' : 'CARRIED_CHECKING') : null
    // A re-upload (AI server): cells that already have their teacher are not applied again —
    // "30 assignments: 26 already assigned, 4 new".
    const alreadyAssigned = awaitingAssignment ? 0 : (carriedCounts()?.alreadyAssigned ?? 0)
    const newAssignments = carriedCounts()?.newAssignments ?? assignments
    const appliedAssignments = alreadyAssigned > 0 ? newAssignments : assignments
    const applied =
      [
        assignments > 0
          ? intl.formatMessage(
              {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.APPLIED_SUMMARY'},
              {teachers: newTeachers, assignments: appliedAssignments}
            )
          : null,
        assignments > 0 && alreadyAssigned > 0
          ? intl.formatMessage(
              {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.UPLOAD.ASSIGNMENT_SPLIT'},
              {total: assignments, already: alreadyAssigned, fresh: newAssignments}
            )
          : null,
        students > 0 ? t('PANEL_STUDENTS_FOUND', {count: students}) : null,
      ]
        .filter(Boolean)
        .join(' ') || null
    switch (panelState) {
      case 'busy': {
        const processed = files.filter((f) => !isFileBusy(f)).length
        if (shownUnique && shownUnique > 0 && processed < total) {
          // More files joined a list already read: its total stays, with what is being read.
          return {
            tone: 'busy',
            kicker: t('PANEL_READING_MORE', {count: total - processed}),
            title: t('PANEL_CURRENT_TOTAL', {count: shownUnique}),
            detail: t('PANEL_READING_PROGRESS', {done: processed, total}),
          }
        }
        return {
          tone: 'busy',
          title: t('PANEL_READING_TITLE', {count: total}),
          detail: t('PANEL_READING_PROGRESS', {done: processed, total}),
        }
      }
      case 'question':
        return {
          tone: 'question',
          title: t('PANEL_QUESTION_TITLE', {count: total}),
          detail: t('PANEL_QUESTION_HINT'),
        }
      case 'ready':
        return {
          tone: 'ready',
          kicker: inList > 0 && newTeachers === 0 ? null : t('PANEL_READY_KICKER'),
          title: readyTitle,
          detail: awaitingAssignment
            ? t('PANEL_QUESTION_INLINE')
            : [
                applied ??
                  t(found > uniqueFileTeachers ? 'PANEL_READY_FILES_MERGED' : 'PANEL_READY_FILES', {
                    count: total,
                  }),
              ]
                .filter(Boolean)
                .join(' '),
          pending,
        }
      case 'partial':
        return {
          tone: 'partial',
          kicker: t('PANEL_PARTIAL_KICKER'),
          title: readyTitle,
          // Failed files, partly read files, or both — with what to do about them (after what the
          // files gave, when they held allocations too).
          detail: awaitingAssignment
            ? t('PANEL_QUESTION_INLINE')
            : [
                applied,
                failed > 0 && partly > 0
                  ? t('PANEL_PARTIAL_BOTH', {failed, partly})
                  : failed > 0
                    ? t('PANEL_PARTIAL_FILES', {count: failed})
                    : t('PANEL_PARTLY_READ', {count: partly}),
              ]
                .filter(Boolean)
                .join(' '),
          pending,
        }
      case 'failed':
        return {
          tone: 'failed',
          title: t('PANEL_FAILED_TITLE', {count: total}),
          detail: t('PANEL_FAILED_HINT'),
        }
      default:
        return {
          tone: 'empty',
          title: t('PANEL_EMPTY_TITLE'),
          detail: t('PANEL_EMPTY_HINT', {count: total}),
        }
    }
  }

  /** One file as the shared uploader shows it: state, status line, progress and its actions. */
  const toUploaderFile = (item: UploadItem): UploaderFile => {
    const {tone, text} = fileState(item)
    return {
      id: item.id,
      name: item.file.name,
      file: item.file,
      state: tone,
      text,
      progress: item.progress ?? null,
      // How long a scan takes is said once, in the photo tips, not under each file being read.
      // A partly read file says how to fix it: the missing names by hand, or a fixed file.
      hint: isPartlyRead(item)
        ? intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.FILE_PARTIAL_HINT'})
        : null,
      // The left-out rows are open on a partly read file: they are what needs fixing.
      details: renderSkipped(item, isPartlyRead(item)),
      // "Try again" only when sending the same file again can help; a damaged or wrong file is
      // replaced instead (QA 13 Oct, #372).
      retryable: item.status === 'error' && item.retryable !== false,
      replaceable: isPartlyRead(item) || (item.status === 'error' && item.retryable === false),
      replaceLabel:
        item.status === 'error'
          ? intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REPLACE_FILE'})
          : undefined,
      actions:
        item.status === 'needs-assignment' ? (
          <button
            type='button'
            className='step4-te-file-action step4-te-file-action--primary'
            onClick={() => setSheetQuestionOpen(true)}
          >
            {intl.formatMessage({id: 'DATA_UPLOAD.SHEETS.ANSWER'})}
          </button>
        ) : null,
    }
  }

  const formatsText = intl.formatMessage({
    id: extractionEnabled
      ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.DROPZONE_FORMATS_ANY'
      : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.DROPZONE_FORMATS',
  })
  const limitsText = intl.formatMessage(
    {
      id: extractionEnabled
        ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_LIMITS_ANY'
        : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_LIMITS',
    },
    {files: maxFiles, perFile: formatBytes(maxFileBytes), total: formatBytes(maxTotalBytes)}
  )

  /**
   * The branch each sheet was put in, with a way to change it: the server never asks an answered
   * sheet again, and a changed answer replaces the branch the old one set.
   */
  const renderBranchAnswers = () => {
    const {answers} = branchAnswered
    const answered = branchAnswered.questions.filter((question) => question.key in answers)
    if (answered.length === 0) return null
    const lines = answered.map((question) => {
      const answer = answers[question.key]
      const branch =
        answer === MIXED_BRANCH
          ? intl.formatMessage({id: 'DATA_UPLOAD.BRANCHES.MIXED'})
          : (question.branches.find((entry) => entry.branchId === answer)?.branch ?? String(answer))
      return `${question.sheet || question.fileName || question.key} → ${branch}`
    })
    return (
      <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3 fs-7'>
        <span className='d-inline-flex align-items-center gap-2 text-muted' style={{minWidth: 0}}>
          <Info size={15} aria-hidden style={{flexShrink: 0}} />
          <span className='text-break'>
            {intl.formatMessage({id: 'DATA_UPLOAD.BRANCHES.ANSWERED'}, {sheets: lines.join(' · ')})}
          </span>
        </span>
        <button
          type='button'
          className='step4-te-text-action'
          onClick={() => {
            // Questions still pending are asked in the same dialog, so none is lost.
            const pendingBranches = (questions?.branches ?? []).filter(
              (question) => !(question.key in answers)
            )
            setQuestions({
              columns: questions?.columns ?? null,
              branches: [...answered, ...pendingBranches],
              subjects: questions?.subjects ?? null,
              allocations: questions?.allocations,
              initialBranches: answers,
            })
            setQuestionsOpen(true)
          }}
        >
          {intl.formatMessage({id: 'DATA_UPLOAD.BRANCHES.CHANGE'})}
        </button>
      </div>
    )
  }

  /** "Whose list is this?" answered (or skipped: every sheet `ignore`): sent once, then kept. */
  const answerSheetQuestion = (assignments: DataUploadSheetAssignments) => {
    setSheetQuestionOpen(false)
    // Kept for every later read: the server asks the same question again without them.
    sheetAnswersRef.current = {...sheetAnswersRef.current, ...assignments}
    // The files waiting for this answer are read now, with the others.
    void readOnServer(files, {includeWaiting: true})
  }

  /**
   * The questions of the last read, closed with the X: still pending, nothing sent. Answering
   * opens the same dialog with the same questions.
   */
  const renderPendingQuestions = () => {
    const count = openQuestionCount(questions)
    if (questionsOpen || count === 0) return null
    const about = questions
      ? pendingQuestionsText(intl.formatMessage, {
          allocations: questions.allocations,
          columns: questions.columns,
          branches: questions.branches,
          answeredBranches: questions.initialBranches,
          subjects: questions.subjects,
        })
      : null
    return (
      <div
        className='d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3 fs-7'
        role='status'
      >
        <span className='d-inline-flex align-items-center gap-2 text-muted' style={{minWidth: 0}}>
          <AlertTriangle size={15} aria-hidden style={{flexShrink: 0}} />
          <span className='text-break'>
            {about ?? intl.formatMessage({id: 'DATA_UPLOAD.QUESTIONS.WAITING'})}
          </span>
        </span>
        <button
          type='button'
          className='btn btn-sm btn-light-primary'
          onClick={() => setQuestionsOpen(true)}
        >
          {/* Only lines confirmed with no classes left: they are reviewed, not answered. */}
          {intl.formatMessage(
            {
              id:
                count === allocationLineCounts(questions?.allocations).review
                  ? 'DATA_UPLOAD.QUESTIONS.REVIEW_COUNT'
                  : 'DATA_UPLOAD.QUESTIONS.ANSWER_COUNT',
            },
            {count}
          )}
        </button>
      </div>
    )
  }

  /** The rows a file left out, and why: collapsed under the file, open when they need fixing. */
  const renderSkipped = (item: UploadItem, open = false) => {
    const skipped = item.skipped ?? []
    if (skipped.length === 0 || (item.status !== 'ready' && item.status !== 'error')) return null
    const unnamed = intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.UNNAMED_ROW'})
    return (
      <details className='step4-te-file-skipped' open={open || undefined}>
        <summary>
          {intl.formatMessage(
            {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.LEFT_OUT'},
            {count: skipped.length}
          )}
        </summary>
        <ul>
          {skipped.slice(0, MAX_LISTED_SKIPPED).map((row, index) => {
            const key = dataUploadSkippedRowKey(row)
            const detail = key ? intl.formatMessage({id: key}) : row.message || row.reason
            return (
              <li key={row.recordId ?? `${row.label}-${index}`}>
                <strong>{row.label || unnamed}</strong>
                {detail ? <span> · {detail}</span> : null}
              </li>
            )
          })}
          {skipped.length > MAX_LISTED_SKIPPED && (
            <li className='step4-te-file-skipped-more'>
              {intl.formatMessage(
                {id: 'DATA_UPLOAD.CONVERT.MORE_ROWS'},
                {count: skipped.length - MAX_LISTED_SKIPPED}
              )}
            </li>
          )}
        </ul>
      </details>
    )
  }

  return (
    <div className={`step4-teacher-entry${isDark ? ' step4-teacher-entry--dark' : ''}`}>
      <div className='step4-te-header'>
        <h2 className='step4-te-title'>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.HEADING'})}
        </h2>
        <p className='step4-te-subtitle'>
          {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.SUBHEADING'})}
        </p>
        {existingCount > 0 && (
          <p className='step4-te-existing'>
            <Info size={14} aria-hidden />
            {intl.formatMessage(
              {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.EXISTING_COUNT'},
              {count: existingCount}
            )}
          </p>
        )}
      </div>

      <div className='step4-te-grid'>
        {/* --- Add / paste names ------------------------------------------------ */}
        <section className='step4-te-card' aria-labelledby={`${textareaId}-title`}>
          <div className='step4-te-card-head'>
            <span className='step4-te-card-icon step4-te-card-icon--paste'>
              <ClipboardList size={22} aria-hidden />
            </span>
            <div className='step4-te-card-head-text'>
              <div className='step4-te-card-title-row'>
                <h3 id={`${textareaId}-title`} className='step4-te-card-title'>
                  {intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PASTE_TITLE',
                  })}
                </h3>
                <span className='step4-te-badge'>
                  {intl.formatMessage({
                    id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.RECOMMENDED',
                  })}
                </span>
              </div>
              <p className='step4-te-card-desc'>
                {intl.formatMessage({
                  id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PASTE_DESCRIPTION',
                })}
              </p>
            </div>
          </div>

          <div className={`step4-te-lines${lineCount === 0 ? ' step4-te-lines--empty' : ''}`}>
            {lineCount > 0 && (
              <div className='step4-te-gutter' ref={gutterRef} aria-hidden>
                {Array.from({length: lineCount}, (_, i) => (
                  <div key={i} className='step4-te-gutter-line'>
                    {i + 1}
                  </div>
                ))}
              </div>
            )}
            <textarea
              id={textareaId}
              ref={textareaRef}
              className='step4-te-textarea'
              wrap='off'
              spellCheck={false}
              autoComplete='off'
              value={namesText}
              placeholder={intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PASTE_PLACEHOLDER',
              })}
              onChange={(e) => setNamesText(e.target.value)}
              onPaste={handlePaste}
              onScroll={syncGutterScroll}
            />
          </div>

          <div className='step4-te-help'>
            {names.length > 0 && (
              <p className='step4-te-help-count'>
                {intl.formatMessage(
                  {id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PASTE_COUNT'},
                  {count: names.length}
                )}
              </p>
            )}
            <p>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PASTE_HELP_SEPARATORS',
              })}
            </p>
            <p>
              {intl.formatMessage({
                id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PASTE_HELP_DUPLICATES',
              })}
            </p>
          </div>
        </section>

        {/* --- Upload ------------------------------------------------------------ */}
        <section className='step4-te-card' aria-labelledby={`${textareaId}-upload-title`}>
          <div className='step4-te-card-head'>
            <span className='step4-te-card-icon step4-te-card-icon--upload'>
              <FileSpreadsheet size={22} aria-hidden />
            </span>
            <div className='step4-te-card-head-text'>
              <h3 id={`${textareaId}-upload-title`} className='step4-te-card-title'>
                {intl.formatMessage({
                  id: singleFile
                    ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_TITLE'
                    : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_TITLE_FILES',
                })}
              </h3>
              <p className='step4-te-card-desc'>
                {intl.formatMessage({
                  id: extractionEnabled
                    ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_DESCRIPTION_ANY'
                    : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_DESCRIPTION',
                })}
              </p>
            </div>
          </div>

          {/* The shared onboarding uploader (same as Teacher Allocation): this page reads names
              first, and also what the files say about classes for the later steps. */}
          <OnboardingUploader
            context='teachers'
            files={files.map(toUploaderFile)}
            head={statusHead()}
            onFiles={addFiles}
            onRemove={removeFile}
            onReplace={removeFile}
            onRetry={(id) => {
              const item = files.find((f) => f.id === id)
              if (item) retryFile(item)
            }}
            busy={isBusy}
            // A file being sent or read: new files wait (as on Teacher Allocation), so a drop
            // never starts a second read over the one in flight.
            disabled={isBusy}
            accept={teacherUploadAccept(extractionEnabled)}
            singleFile={singleFile}
            formatsText={formatsText}
            limitsText={limitsText}
            onDownloadTemplate={onDownloadTemplate}
            rejected={rejectedFiles}
            onDismissRejected={() => setRejectedFiles([])}
            guidance={
              // Short guidance instead of a long note: one line, and photo tips on demand.
              <div className='step4-te-guide'>
                <p className='step4-te-guide-note'>
                  <Info size={16} aria-hidden />
                  <span>
                    {intl.formatMessage({
                      id: extractionEnabled
                        ? 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.SCAN_NOTE'
                        : 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.UPLOAD_NOTE_EXTRACT',
                    })}
                  </span>
                </p>
                {extractionEnabled && (
                  <details className='step4-te-tips'>
                    <summary>
                      <ChevronRight size={15} aria-hidden className='step4-te-tips-chevron' />
                      {intl.formatMessage({
                        id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PHOTO_TIPS_TOGGLE',
                      })}
                    </summary>
                    <ul>
                      <li>
                        {intl.formatMessage({
                          id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PHOTO_TIP_FRAME',
                        })}
                      </li>
                      <li>
                        {intl.formatMessage({
                          id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PHOTO_TIP_LIGHT',
                        })}
                      </li>
                      <li>
                        {intl.formatMessage({
                          id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.PHOTO_TIP_SIDEWAYS',
                        })}
                      </li>
                      <li>{intl.formatMessage({id: 'DATA_UPLOAD.CONVERT.READING_FILE_SLOW'})}</li>
                    </ul>
                  </details>
                )}
              </div>
            }
          >
            {!TEACHER_FILE_SUMMARY_ON_REVIEW && !isBusy && renderSummary()}
            {!isBusy && renderPendingQuestions()}
            {!isBusy && renderBranchAnswers()}
          </OnboardingUploader>
        </section>
      </div>

      <div className='step4-te-footer'>
        {showBackButton && (
          <button type='button' className='onboarding-btn-back' onClick={onBack}>
            <ArrowLeft size={16} className='me-2' />
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.FOOTER.BACK'})}
          </button>
        )}
        <div className='step4-te-footer-cta'>
          <button
            type='button'
            className='onboarding-btn-next'
            disabled={!canReview || reviewQueued}
            aria-busy={reviewQueued || undefined}
            onClick={handleReview}
          >
            {reviewQueued && (
              <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />
            )}
            {intl.formatMessage({id: 'ONBOARDING_WIZARD.STEP4_MANUAL.TEACHERS.ENTRY.REVIEW_BTN'})}
            <ArrowRight size={16} className='ms-2' />
          </button>
          <span className='step4-te-footer-hint'>{intl.formatMessage({id: reviewHintKey})}</span>
        </div>
      </div>

      {/* One dialog for every file (they are read in one request). Closing without answering
          (the X) leaves the files waiting, with nothing sent: their status line offers the
          question again, so a workbook is never read as the wrong screen's list by default.
          Skip answers every sheet "Don't read it" (`ignore`), sent like any answer. */}
      <SheetAssignmentModal
        show={sheetQuestionOpen && Boolean(sheetQuestion)}
        question={sheetQuestion}
        files={files.map((item) => item.file)}
        onCancel={() => setSheetQuestionOpen(false)}
        onClose={() => setSheetQuestionOpen(false)}
        onConfirm={answerSheetQuestion}
        onSkip={answerSheetQuestion}
      />

      {/* After the rows appear, in one dialog: "which field is this column?", "which branch
          are these students in?" and "Maths -> Mathematics". Confirm saves the ticked subjects
          for the school, then reads the files again ONCE with every answer. Skip answers each
          question with its skip value, sent ONCE and kept, so none is asked again. The X only
          closes it: the questions stay pending ("Answer questions" on the files), nothing is
          sent. */}
      <ColumnAssignmentModal
        show={questionsOpen && Boolean(questions)}
        columns={questions?.columns ?? null}
        branches={questions?.branches ?? NO_BRANCH_QUESTIONS}
        initialBranches={questions?.initialBranches}
        subjects={questions?.subjects ?? null}
        allocations={questions?.allocations}
        proposalReview={ONBOARDING_QA_OCT18}
        // Subjects with no class: save the teachers only (the default), or pick classes now.
        peopleOnly={(questions?.allocations?.length ?? 0) > 0 ? 'teachers' : undefined}
        files={files.map((item) => item.file)}
        submitting={answeringQuestions}
        onClose={() => {
          setQuestionsOpen(false)
          // Only branches reopened to be changed, none open: nothing is left to come back to.
          if (openQuestionCount(questions) === 0) setQuestions(null)
        }}
        onSkip={(skipped) => {
          setQuestions(null)
          setQuestionsOpen(false)
          // A branch has no skip value, and a skipped subject is not saved: kept here instead.
          skipped.branches.forEach((key) => skippedBranchKeysRef.current.add(key))
          skipped.subjects.forEach((written) =>
            skippedSubjectsRef.current.add(written.toLowerCase())
          )
          // Columns skipped: `ignore`, sent with the column answers from now on.
          const skippedColumns = Object.keys(skipped.columns).length > 0
          if (skippedColumns) {
            columnAnswersRef.current = {...columnAnswersRef.current, ...skipped.columns}
          }
          // "Which classes?" skipped: those subjects are not allocated (answered with no class,
          // for extract-teachers and the prefill alike, on every later read), so neither this
          // step nor the Teacher Allocation step asks them again.
          if (skipped.allocations.length > 0) {
            const skippedKeys = new Set(skipped.allocations.map(allocationAnswerKey))
            allocationAnswersRef.current = [
              ...allocationAnswersRef.current.filter(
                (answer) => !skippedKeys.has(allocationAnswerKey(answer))
              ),
              ...skipped.allocations,
            ]
            onAllocationAnswers?.(allocationAnswersRef.current)
          }
          // The skip answers the server keeps go ONCE, in one read of the files.
          if (skippedColumns || skipped.allocations.length > 0) void readOnServer(files)
        }}
        onConfirm={(answers) => {
          columnAnswersRef.current = {...columnAnswersRef.current, ...answers.columns}
          answers.subjects.forEach((mapping) =>
            subjectAnswersRef.current.set(mapping.written.toLowerCase(), mapping)
          )
          branchAnswersRef.current = {...branchAnswersRef.current, ...answers.branches}
          /** Classes were sent before: the files are read again without them. */
          let droppedClasses = false
          if (answers.peopleOnly) {
            // No class answers go anywhere; the Allocation step asks them itself. Lines skipped
            // earlier stay skipped (no class): they are not to be allocated, nor asked again.
            teachersOnlyRef.current = true
            const skippedLines = allocationAnswersRef.current.filter(
              (answer) => answer.classIds.length === 0
            )
            droppedClasses = skippedLines.length < allocationAnswersRef.current.length
            allocationAnswersRef.current = skippedLines
          } else {
            if ((questions?.allocations?.length ?? 0) > 0) teachersOnlyRef.current = false
            // A re-answered teacher + subject replaces its earlier answer.
            const answeredNow = new Set(answers.allocations.map(allocationAnswerKey))
            allocationAnswersRef.current = [
              ...allocationAnswersRef.current.filter(
                (answer) => !answeredNow.has(allocationAnswerKey(answer))
              ),
              ...answers.allocations,
            ]
          }
          onAllocationAnswers?.(allocationAnswersRef.current)
          if (questions && Object.keys(answers.branches).length > 0) {
            // Kept so an answer can be changed later; a re-answered sheet replaces its entry.
            const asked = new Set(questions.branches.map((question) => question.key))
            setBranchAnswered((prev) => ({
              questions: [
                ...prev.questions.filter((question) => !asked.has(question.key)),
                ...questions.branches,
              ],
              answers: {...prev.answers, ...answers.branches},
            }))
          }
          const reread = () => {
            setAnsweringQuestions(false)
            setQuestions(null)
            void readOnServer(files)
          }
          // "Save teachers only" and nothing else answered: the rows already read stand.
          if (
            answers.peopleOnly &&
            !droppedClasses &&
            Object.keys(answers.columns).length === 0 &&
            Object.keys(answers.branches).length === 0 &&
            answers.subjects.length === 0
          ) {
            setQuestions(null)
            setQuestionsOpen(false)
            return
          }
          if (answers.subjects.length === 0) {
            reread()
            return
          }
          setAnsweringQuestions(true)
          dispatchApi(dispatch, (ok, fail) =>
            putDataUploadSubjectAliases(subjectAliasesBody(answers.subjects), ok, fail)
          )
            .then((res) => {
              const rejected = rejectedSubjectAliasCount(res)
              if (rejected > 0) {
                toast.warning(
                  intl.formatMessage({id: 'DATA_UPLOAD.SUBJECTS.REJECTED'}, {count: rejected})
                )
              }
              reread()
            })
            .catch((error) => {
              setAnsweringQuestions(false)
              toast.error(
                dataUploadErrorMessage(intl.formatMessage, error, {
                  config: uploadConfig,
                  useCaseKey: 'teachers',
                })
              )
            })
        }}
      />
    </div>
  )
}

export default Step4TeacherEntry
